import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { ExecutorIntakeForm } from "@/components/executor-intake-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { EnquiryForm } from "@/components/enquiry-form";
import { FIRM, PENINSULA_ADDRESS, faqSchema } from "@/lib/firm";
import { ESTATE_DISPUTE_LOCATIONS } from "@/lib/estate-dispute-locations";


const FAQS = [
  {
    q: "Who can contest a will in Queensland?",
    a: "A spouse (including a de facto partner and, in some cases, a former spouse being maintained), a child (including adopted children and stepchildren in defined circumstances), and a dependant who was wholly or substantially maintained by the deceased. Anyone outside those categories is not eligible, however unfair the will feels.",
  },
  {
    q: "How long do I have to contest a will in Queensland?",
    a: "Written notice of your intention must reach the executor within six months of the date of death, and the application must be filed in court within nine months of the date of death. Both clocks run from death, not from the grant of probate. The court can extend time, but it is discretionary and much harder once the estate has been distributed.",
  },
  {
    q: "What is the difference between contesting a will and challenging it?",
    a: "A family provision claim accepts that the will is valid and asks the court for a larger share. A validity challenge says the will should not operate at all — because of a lack of testamentary capacity, no knowledge and approval, undue influence, improper execution or forgery. They need different evidence and can be run together in the alternative.",
  },
  {
    q: "Do I have to go to court?",
    a: "Usually not. The application is filed in court, but the overwhelming majority of Queensland family provision claims are resolved at mediation or in negotiation before a hearing. Our aim is to resolve your matter at the earliest stage that produces a fair outcome.",
  },
  {
    q: "I am the executor and a claim has been made. What should I do?",
    a: `Do not distribute the estate, respond to the notice, and get advice. An executor who distributes with knowledge of a foreshadowed claim can become personally liable. Your reasonable costs of properly defending the estate are generally payable from it. Call ${FIRM.phone} before you release any funds.`,
  },
  {
    q: "Does the estate pay my legal costs?",
    a: "Not automatically. Costs are in the court's discretion. Successful applicants often have costs paid from the estate, but unsuccessful applicants — particularly in small estates — can be left paying their own and sometimes the estate's. We give you a frank view of the risk before anything is filed.",
  },
];

const CLAIMANTS = [
  "Family provision applications for spouses, de facto partners and former partners",
  "Claims by children, adult children and stepchildren left out or inadequately provided for",
  "Claims by dependants who were being maintained by the deceased",
  "Challenges to a will's validity — capacity, undue influence, suspicious circumstances",
  "Caveats against a grant of probate where a will is in question",
  "Disputes over informal wills, notes and unsigned documents",
];

const EXECUTORS = [
  "Responding to a notice of intention and protecting you from personal liability",
  "Defending the estate at mediation and, where necessary, at trial",
  "Applications for directions, and advice on interim distributions",
  "Removal or replacement of an executor who will not act",
  "Estate accounts, beneficiary demands and claims of executor misconduct",
  "Recovering estate assets and dealing with contested lifetime transfers",
];

const CANONICAL = "https://coastsidelaw.com.au/estate-disputes";

export const Route = createFileRoute("/estate-disputes/")({
  head: () => ({
    meta: [
      { title: fitTitle(`Estate Dispute Lawyers Caloundra | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Contesting and defending wills in Queensland. Family provision claims, validity challenges and executor disputes, from our Golden Beach office at Caloundra."),
      },
      { property: "og:title", content: fitTitle(`Estate Dispute Lawyers | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Family provision claims, will validity challenges and executor disputes across Caloundra, the Sunshine Coast and Moreton Bay."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: EstateDisputesPage,
});

function EstateDisputesPage() {
  return (
    <>
      <PageHero
        eyebrow="Wills &amp; estates"
        title="Estate disputes — contesting a will, and defending one"
        lead="Family provision claims, challenges to a will's validity and disputes between executors and beneficiaries. Two deadlines run from the date of death, so the first call matters more than the last. We act for claimants and for executors across Caloundra, the Sunshine Coast and Moreton Bay."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <div className="rounded-sm border-l-2 border-accent bg-secondary/50 p-6">
          <p className="text-sm leading-relaxed text-foreground">
            In Queensland you must give the executor written notice within{" "}
            <strong>six months</strong> of the date of death, and file any family provision
            application within <strong>nine months</strong>. Both run from death, not from probate.
            If you are near either date, call {FIRM.phone} today rather than waiting for an
            appointment.
          </p>
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">If you have been left out</h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          An unequal or exclusionary will is not automatically unlawful. The question the court asks
          is whether adequate provision was made for your proper maintenance and support, weighed
          against your financial position, the size of the estate, your contributions and the
          competing claims of other beneficiaries. We will tell you honestly at the first appointment
          whether a claim is worth running.
        </p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {CLAIMANTS.map((item) => (
            <li
              key={item}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              {item}
            </li>
          ))}
        </ul>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          If you are the executor defending the estate
        </h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Executors carry personal exposure if they distribute while a claim is on foot, and they are
          often dealing with grieving beneficiaries at the same time. We take that pressure off you,
          deal with the claimant's solicitors, and keep the estate's costs proportionate to what is
          actually in dispute.
        </p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {EXECUTORS.map((item) => (
            <li
              key={item}
              className="rounded-sm border border-border bg-card px-4 py-3 text-sm text-muted-foreground"
            >
              {item}
            </li>
          ))}
        </ul>

        <div className="mt-16 rounded-sm border border-border bg-secondary/50 p-7">
          <h2 className="font-serif text-2xl text-foreground">Read before you decide</h2>
          <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
            Our plain-English guides cover each part of a Queensland estate dispute.
          </p>
          <ul className="mt-5 space-y-2 text-sm font-semibold">
            <li>
              <Link
                to="/guides/$slug"
                params={{ slug: "contesting-a-will-in-queensland" }}
                className="text-primary underline"
              >
                Contesting a will in Queensland: who can, and the time limits
              </Link>
            </li>
            <li>
              <Link
                to="/guides/$slug"
                params={{ slug: "time-limits-contesting-a-will-queensland" }}
                className="text-primary underline"
              >
                Time limits for contesting a will in Queensland
              </Link>
            </li>
            <li>
              <Link
                to="/guides/$slug"
                params={{ slug: "parent-left-everything-to-one-child-queensland" }}
                className="text-primary underline"
              >
                My parent left everything to one child — what can I do?
              </Link>
            </li>
            <li>
              <Link
                to="/guides/$slug"
                params={{ slug: "challenging-a-will-capacity-undue-influence-queensland" }}
                className="text-primary underline"
              >
                Challenging a will's validity: capacity and undue influence
              </Link>
            </li>
            <li>
              <Link
                to="/guides/$slug"
                params={{ slug: "how-much-does-it-cost-to-contest-a-will-queensland" }}
                className="text-primary underline"
              >
                How much does it cost to contest a will?
              </Link>
            </li>
            <li>
              <Link
                to="/guides/$slug"
                params={{ slug: "estate-contested-executor-guide-queensland" }}
                className="text-primary underline"
              >
                Executor's guide to a contested estate
              </Link>
            </li>
          </ul>
        </div>

        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Estate disputes by area
        </h2>
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          We act for claimants and executors across the Sunshine Coast and Moreton Bay, with a
          peninsula office at {PENINSULA_ADDRESS} by appointment.
        </p>
        <ul className="mt-5 flex flex-wrap gap-2">
          {ESTATE_DISPUTE_LOCATIONS.map((l) => (
            <li key={l.slug}>
              <Link
                to="/estate-disputes/$suburb"
                params={{ suburb: l.slug }}
                className="inline-block rounded-sm border border-border bg-card px-3 py-1.5 text-sm text-foreground transition-colors hover:border-primary"
              >
                Contesting a will {l.name}
              </Link>
            </li>
          ))}
        </ul>

        <PeopleStrip slugs={["michael-klein", "erin-wilkinson"]} heading="Who will act for you" />


        <h2 className="mt-16 font-serif text-3xl text-foreground">
          Questions about contesting a will in Queensland
        </h2>
        <div className="mt-8">
          <FaqList items={FAQS} />
        </div>

        <div className="mt-16">
          <EnquiryForm heading="Talk to us about an estate dispute" />
        </div>
        <div className="mt-14">
          <ExecutorIntakeForm heading="Estate or executor enquiry" />
        </div>
      </section>

      <CtaBand
        title="Close to the six or nine month deadline?"
        body={`Call ${FIRM.phone}. Even a short conversation now can protect your position while we look at the will properly.`}
      />
      <div className="h-20" />
    </>
  );
}
